import type { FC, ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import type { ToastInfos } from "tw/components/molecules/Toast";
import Toast from "tw/components/molecules/Toast";

type ToastContextType = {
  isToastOpen: boolean;
  showToast: (toast: ToastInfos) => void;
  updateToast: (props: Partial<ToastInfos>) => void;
  closeToast: () => void;
};

const ToastContext = createContext<ToastContextType>({
  isToastOpen: false,
  showToast: () => {},
  updateToast: () => {},
  closeToast: () => {},
});

export const ToastProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [toast, setToast] = useState<ToastInfos | null>(null);

  const showToast = useCallback((newToast: ToastInfos) => {
    setToast(newToast);
  }, []);

  const updateToast = useCallback((props: Partial<ToastInfos>) => {
    setToast((current) => (current ? { ...current, ...props } : null));
  }, []);

  const closeToast = useCallback(() => {
    setToast(null);
  }, []);

  const contextValues = useMemo(
    () => ({ isToastOpen: !!toast, showToast, updateToast, closeToast }),
    [toast, showToast, updateToast, closeToast]
  );

  return (
    <ToastContext.Provider value={contextValues}>
      {children}
      {toast && <Toast toast={toast} closeToast={closeToast} />}
    </ToastContext.Provider>
  );
};

export const useToast = () => useContext(ToastContext);
